import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import HoverTooltip from 'src/components/BeaconV2Components/HoverTooltip/HoverTooltip';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import useAdvancedSearchData from 'src/serverProxy/useAdvancedSearchData';

import { useAppSelector, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import { formatMetric } from 'src/utils/app';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { getTotalWeekInTheYear } from 'src/utils/dateUtils';
import { getWeekLastDay } from 'src/utils/dateformatting';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';

const parseData = (data: any[], startWeek: number) => {
  const [volumeData] = data.map((d) => d?.data[0]?.aggregations?.by_weekId || []);

  if (!volumeData || volumeData.length === 0) {
    return { currentData: [], exportCurrentData: [] };
  }

  const sortByWeekId = (arr) => arr.sort((a, b) => a.weekId - b.weekId);

  const sortedVolumeData = sortByWeekId(volumeData);

  const dataByWeekId = new Map<number, any>();
  sortedVolumeData.forEach((d) => dataByWeekId.set(Number(d.weekId), d));

  const fillFullYearWeekIds = () => {
    const full52WeeksWeekIds = [];
    let year = Math.floor(startWeek / 100);
    let week = startWeek % 100;

    while (full52WeeksWeekIds.length < 52) {
      const maxWeeks = getTotalWeekInTheYear(year);
      if (week > maxWeeks) {
        year += 1;
        week = 1;
      }

      const numericWeekId = year * 100 + week;
      const matchedData = dataByWeekId.get(numericWeekId);

      full52WeeksWeekIds.push({
        fieldName: 'weekId',
        fieldId: matchedData ? matchedData.weekId : String(numericWeekId),
        weekId: matchedData ? matchedData.weekId : numericWeekId,
        value: matchedData ? matchedData.value : 0
      });

      week += 1;
    }

    return full52WeeksWeekIds;
  };

  const mapDataToWeeks = (weekData) => weekData.map((d) => [(getWeekLastDay as any)(d.weekId), d.value]);

  const currentData = mapDataToWeeks(fillFullYearWeekIds());
  return {
    currentData,
    exportCurrentData: mapDataToWeeks(sortedVolumeData)
  };
};

const YAxisLabel = ({ value }: { value: number }) => {
  return (
    <div>
      {formatMetric(Number(value), METRICTYPE.INTEGER, {
        showFullValue: false
      })}
    </div>
  );
};

/**
 * Weekly Volume chart grouped by weekId.
 *
 */
const WeeklyReviewProductChart = () => {
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const theme = useStacklineTheme();

  const YAxisTextStyles: Highcharts.CSSObject = {
    fontFamily: theme.text.fontFamily,
    color: theme.colors.primary,
    fontSize: '12px'
  };

  const { startWeek } = useAppSelector((state) => state.mainTimePeriod);

  const { data, isLoading } = useAdvancedSearchData({
    fields: [{ name: 'weeklyReviewVolume' }],
    indexName: `${BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2}`,
    groupByField: 'weekId',
    requestId: 'weekly-review-volume-columnChart',
    shouldFetchComparison: false,
    returnRawResponse: true
  });

  const { currentData, exportCurrentData } = !isLoading
    ? parseData(data as any[], startWeek)
    : {
        currentData: [],
        exportCurrentData: []
      };

  // This is used for YAxis - sometimes YAxis can be just 1, or 2, when design mock has 5 minimum
  const maxValue = useMemo(() => {
    let currentMax = 0;
    currentData.forEach(([, value]: [unknown, string | number]) => {
      if (typeof value === 'number') {
        currentMax = Math.max(currentMax, value);
      }
    });
    return currentMax;
  }, [currentData]);

  return (
    <BeaconChartWithLegend
      title="Weekly Review Volume"
      includeSummaryInfo={false}
      subtitleSX={{
        marginTop: '8px'
      }}
      convertSeriesToDelimitedData={async () => {
        return saveSingleBarChartAsDelimitedData(exportCurrentData, {
          metricLabel: 'Weekly Review Volume'
        });
      }}
      primaryLegendProps={{
        displayName: legendLabel
      }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the review volume for ${entityPhrase} `}
            {taxonomyText} {`during the ${mainTimePeriodShortDisplayName} period`}
            .
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      loading={isLoading}
      loadingComponent={<SplineChartLoading width={1000} />}
    >
      <TopEntityChartV2
        reverseAxisStartPosition
        useStaticCategory
        maxDataPointsToShow={51}
        leftAlignBars
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(<GenericXAxisLabel xAxisCategory={xAxisCategory} />);
        }}
        seriesColors={[theme.colors.primary]}
        chartSeries={[
          {
            type: 'column',
            data: currentData,
            color: theme.colors.primary,
            metricType: METRICTYPE.VOLUME,
            categories: [{ type: 'weekId' }]
          }
        ]}
        chartOptionsOverride={(option) => {
          const clonedOption = { ...option };
          clonedOption.chart.marginTop = 10;
          clonedOption.xAxis[0].max = 51;
          clonedOption.xAxis[0].labels.step = 5;
          clonedOption.yAxis = Array.from({ length: 1 }).map(() => ({
            gridLineWidth: 0,
            title: {
              text: ''
            },
            offset: 13,
            labels: {
              formatter() {
                const { value } = this;
                return ReactDOMServer.renderToStaticMarkup(<YAxisLabel value={Number(value)} />);
              },
              style: YAxisTextStyles
            },
            tickAmount: Math.min(5, maxValue)
          }));
          const series = clonedOption.series[0] as any;
          series.pointWidth = 8;
          series.pointPadding = 0;
          series.groupPadding = 0;
          series.enableMouseTracking = true;
          (clonedOption.plotOptions.series.dataLabels as any).enabled = false;
          clonedOption.tooltip = {
            enabled: true,
            shared: true,
            shadow: false,
            hideDelay: 0,
            outside: true,
            backgroundColor: 'transparent',
            borderColor: 'transparent',
            useHTML: true,
            formatter() {
              return ReactDOMServer.renderToStaticMarkup(
                <HoverTooltip
                  theme={theme}
                  title={this.points[0]?.key as unknown as string}
                  primaryColor={theme.colors.primary}
                  primaryMetric={`${formatMetric(this.y, METRICTYPE.VOLUME, {
                    decimalPlaces: 2
                  })}`}
                />
              );
            }
          };

          return clonedOption;
        }}
      />
    </BeaconChartWithLegend>
  );
};

export default WeeklyReviewProductChart;
